// src/pages/NornsWeave.js - Enhanced Teacher Portal
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { sendBookingRequestEmail, sendBookingCancellationEmail, sendTeacherLateCancellationEmail } from '../utils/emailService';
import TimePicker, { formatTime12 } from '../components/TimePicker';

function NornsWeave() {
  const [authenticated, setAuthenticated] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [fixedClasses, setFixedClasses] = useState([]);
  const [view, setView] = useState('calendar');

  // Academic Year Setup Data
  const [holyDays, setHolyDays] = useState([]);
  const [adorationDays, setAdorationDays] = useState([]);
  const [schoolCalendarEvents, setSchoolCalendarEvents] = useState([]);
  const [massTimeBlocks, setMassTimeBlocks] = useState([]);
  const [yearSetupSettings, setYearSetupSettings] = useState({
    massDay: 'Thursday',
    massStartTime: '09:00',
    massEndTime: '10:00',
    academicYearStart: null,
    academicYearEnd: null,
    schoolStartDate: null,
    schoolEndDate: null
  });
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [activeLibrary, setActiveLibrary] = useState('elementary');

  // My Bookings tab state
  const [myBookingsEmail, setMyBookingsEmail] = useState('');
  const [myBookings, setMyBookings] = useState([]);
  const [myFixedClasses, setMyFixedClasses] = useState([]);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);
  const [myBookingsError, setMyBookingsError] = useState('');

  const [bookingForm, setBookingForm] = useState({
    type: 'class', // 'admin' or 'class'
    teacherName: '',
    teacherEmail: '',
    grade: '',
    studentCount: '',
    purpose: '',
    needsLibrarian: false,
    lessonRequest: '',
    notes: '',
    library: 'elementary',
    date: '',
    startTime: '',
    endTime: '',
    isRecurring: false,
    recurringDay: 'Monday',
    recurringEndDate: '',
    services: {
      researchSupport: false,
      bookCheckout: false,
      projectorScreen: false,
      bookSetsNeeded: false,
      bookSetsDetails: '',
      aideHelpRequested: false,
      aideHelpDetails: '',
      other: false,
      otherDetails: ''
    }
  });

  useEffect(() => {
    const storedAuth = sessionStorage.getItem('nornsWeaveAuth');
    const storedUserId = sessionStorage.getItem('nornsWeaveUserId');
    if (storedAuth === 'true' && storedUserId) {
      setAuthenticated(true);
      loadSettings(storedUserId);
    }
  }, []);

  useEffect(() => {
    if (authenticated && settings) {
      loadBookings();
      loadPendingBookings();
      loadFixedClasses();
      loadYearSetupData();
    }
  }, [authenticated, settings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use secure Cloud Function for authentication (passwords are hashed)
      const authUrl = process.env.NODE_ENV === 'production'
        ? 'https://us-central1-heimdl.cloudfunctions.net/authenticateNornsWeaveUser'
        : 'http://localhost:5001/heimdl/us-central1/authenticateNornsWeaveUser';

      const response = await fetch(authUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: usernameInput,
          password: passwordInput,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError('Invalid username or password. Please check your credentials.');
        setLoading(false);
        return;
      }

      const userId = result.userId;

      const settingsDoc = await getDoc(doc(db, 'users', userId, 'settings', 'nornsWeave'));

      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
        setAuthenticated(true);
        sessionStorage.setItem('nornsWeaveAuth', 'true');
        sessionStorage.setItem('nornsWeaveUserId', userId);
      } else {
        setError('Portal settings not found. Please contact your librarian.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login error. Please contact your librarian.');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async (userId) => {
    try {
      const settingsDoc = await getDoc(doc(db, 'users', userId, 'settings', 'nornsWeave'));
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const loadBookings = async () => {
    try {
      const userId = sessionStorage.getItem('nornsWeaveUserId');
      if (!userId) return;

      const bookingsRef = collection(db, 'users', userId, 'nornsWeaveBookings');
      const snapshot = await getDocs(bookingsRef);
      const bookingsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBookings(bookingsList);
    } catch (err) {
      console.error('Error loading bookings:', err);
    }
  };

  const loadPendingBookings = async () => {
    try {
      const userId = sessionStorage.getItem('nornsWeaveUserId');
      if (!userId) return;

      const pendingRef = collection(db, 'users', userId, 'nornsWeavePending');
      const snapshot = await getDocs(pendingRef);
      const pendingList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPendingBookings(pendingList);
    } catch (err) {
      console.error('Error loading pending bookings:', err);
    }
  };

  const loadFixedClasses = async () => {
    try {
      const userId = sessionStorage.getItem('nornsWeaveUserId');
      if (!userId) return;

      const classesRef = collection(db, 'users', userId, 'nornsWeaveClasses');
      const snapshot = await getDocs(classesRef);
      const classesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFixedClasses(classesList);
    } catch (err) {
      console.error('Error loading fixed classes:', err);
    }
  };

  // Load academic year setup data (holy days, adoration, holidays, etc)
  const loadYearSetupData = async () => {
    try {
      const userId = sessionStorage.getItem('nornsWeaveUserId');
      if (!userId) return;

      // Load year setup settings from main settings
      const settingsDoc = await getDoc(doc(db, 'users', userId, 'settings', 'nornsWeave'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        if (data.academicYear || data.massSchedule) {
          setYearSetupSettings({
            massDay: data.massSchedule?.regularDay || 'Thursday',
            massStartTime: data.massSchedule?.startTime || '09:00',
            massEndTime: data.massSchedule?.endTime || '10:00',
            academicYearStart: data.academicYear?.startDate || null,
            academicYearEnd: data.academicYear?.endDate || null,
            schoolStartDate: data.academicYear?.schoolStartDate || null,
            schoolEndDate: data.academicYear?.schoolEndDate || null
          });
        }
      }

      // Load holy days
      const holyDaysRef = collection(db, 'users', userId, 'nornsWeaveHolyDays');
      const holyDaysSnapshot = await getDocs(holyDaysRef);
      setHolyDays(holyDaysSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load adoration days
      const adorationRef = collection(db, 'users', userId, 'nornsWeaveAdorationDays');
      const adorationSnapshot = await getDocs(adorationRef);
      setAdorationDays(adorationSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load school calendar events
      const calendarRef = collection(db, 'users', userId, 'nornsWeaveSchoolCalendar');
      const calendarSnapshot = await getDocs(calendarRef);
      setSchoolCalendarEvents(calendarSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load mass time blocks
      const massBlocksRef = collection(db, 'users', userId, 'nornsWeaveMassTimeBlocks');
      const massBlocksSnapshot = await getDocs(massBlocksRef);
      setMassTimeBlocks(massBlocksSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading year setup data:', err);
    }
  };

  // Search for bookings by email address
  const searchMyBookings = async (email) => {
    if (!email || !email.includes('@')) {
      setMyBookingsError('Please enter a valid email address.');
      return;
    }

    setMyBookingsLoading(true);
    setMyBookingsError('');

    try {
      const userId = sessionStorage.getItem('nornsWeaveUserId');
      if (!userId) {
        setMyBookingsError('Session expired. Please log in again.');
        setMyBookingsLoading(false);
        return;
      }

      // Search approved bookings
      const bookingsRef = collection(db, 'users', userId, 'nornsWeaveBookings');
      const bookingsSnapshot = await getDocs(bookingsRef);
      const allBookings = bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const userBookings = allBookings.filter(b =>
        b.teacherEmail?.toLowerCase() === email.toLowerCase()
      );

      // Search pending bookings
      const pendingRef = collection(db, 'users', userId, 'nornsWeavePending');
      const pendingSnapshot = await getDocs(pendingRef);
      const allPending = pendingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), status: 'pending' }));
      const userPending = allPending.filter(b =>
        b.teacherEmail?.toLowerCase() === email.toLowerCase()
      );

      // Search fixed classes
      const fixedRef = collection(db, 'users', userId, 'nornsWeaveFixedClasses');
      const fixedSnapshot = await getDocs(fixedRef);
      const allFixed = fixedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const userFixed = allFixed.filter(c =>
        c.teacherEmail?.toLowerCase() === email.toLowerCase()
      );

      // Combine and set results
      setMyBookings([...userBookings, ...userPending]);
      setMyFixedClasses(userFixed);

      if (userBookings.length === 0 && userPending.length === 0 && userFixed.length === 0) {
        setMyBookingsError('No bookings found for this email address.');
      }
    } catch (err) {
      console.error('Error searching bookings:', err);
      setMyBookingsError('Error searching bookings. Please try again.');
    } finally {
      setMyBookingsLoading(false);
    }
  };

  // Cancel a booking from My Bookings tab
  const handleCancelMyBooking = async (booking) => {
    if (!window.confirm(`Are you sure you want to cancel your booking on ${booking.date} at ${booking.startTime}?`)) {
      return;
    }

    try {
      const userId = sessionStorage.getItem('nornsWeaveUserId');
      if (!userId) return;

      // Check for late cancellation before deleting (only for approved bookings)
      if (booking.status === 'approved' && settings?.libraryChairSettings?.notifyOnLateTeacherCancel) {
        const libraryChairs = settings.libraryChairs || [];
        if (libraryChairs.length > 0) {
          // Calculate days notice
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const classDate = new Date(booking.date + 'T00:00:00');
          const diffTime = classDate - today;
          const daysNotice = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          // Check if this is a late cancellation
          const lateNoticeDays = settings.libraryChairSettings?.lateNoticeDays || 7;
          if (daysNotice < lateNoticeDays) {
            console.log(`Late cancellation detected: ${daysNotice} days notice (threshold: ${lateNoticeDays})`);

            // Send notification to each library chair
            for (const chair of libraryChairs) {
              if (!chair.email) continue;
              try {
                await sendTeacherLateCancellationEmail({
                  chairEmail: chair.email,
                  grade: booking.grade,
                  subject: booking.subject || booking.purpose || '',
                  teacherName: booking.teacherName,
                  classDate: booking.date,
                  classTime: `${formatTime12(booking.startTime)} - ${formatTime12(booking.endTime)}`,
                  library: booking.library,
                  daysNotice: daysNotice,
                  librarianName: settings.yourName,
                  schoolName: settings.schoolName,
                  librarianEmail: settings.librarianEmail,
                });
                console.log(`Late cancellation notification sent to ${chair.email}`);
              } catch (error) {
                console.error(`Failed to send late cancellation email to ${chair.email}:`, error);
              }
            }
          }
        }
      }

      // Delete from appropriate collection based on status
      if (booking.status === 'pending') {
        await deleteDoc(doc(db, 'users', userId, 'nornsWeavePending', booking.id));
      } else {
        await deleteDoc(doc(db, 'users', userId, 'nornsWeaveBookings', booking.id));
      }

      // Send cancellation email
      if (settings?.emailEnabled && settings?.librarianEmail) {
        try {
          await sendBookingCancellationEmail(booking, settings);
        } catch (emailErr) {
          console.error('Failed to send cancellation email:', emailErr);
        }
      }

      // Refresh the list
      searchMyBookings(myBookingsEmail);
    } catch (err) {
      console.error('Error cancelling booking:', err);
      alert('Failed to cancel booking. Please try again.');
    }
  };

  const validateNoticePeriod = (bookingDate, bookingType) => {
    const now = new Date();
    const booking = new Date(bookingDate);
    const hoursUntilBooking = (booking - now) / (1000 * 60 * 60);

    if (bookingType === 'admin') {
      if (hoursUntilBooking < 48) {
        return {
          valid: false,
          message: 'Admin bookings require at least 48 hours notice.'
        };
      }
    } else {
      const daysUntilBooking = hoursUntilBooking / 24;
      if (daysUntilBooking < 7) {
        return {
          valid: false,
          message: 'Class bookings require at least 1 week notice.'
        };
      }
    }

    return { valid: true };
  };

  const checkLibrarianAvailability = (date, startTime, endTime) => {
    if (!settings) return { available: true };

    const dayName = getDayName(date);

    // Check librarian blocks
    const blocks = settings.librarianBlocks || [];
    for (const block of blocks) {
      if (matchesDay(block.day, dayName) && doTimesOverlap(startTime, endTime, block.startTime, block.endTime)) {
        return {
          available: false,
          reason: `The librarian isn't available during ${block.reason || 'this time'}. Please select a different slot or choose an independent visit.`
        };
      }
    }

    return { available: true };
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userId = sessionStorage.getItem('nornsWeaveUserId');
      if (!userId) {
        alert('Session expired. Please log in again.');
        return;
      }

      // Validate notice period
      const noticeCheck = validateNoticePeriod(bookingForm.date, bookingForm.type);
      if (!noticeCheck.valid) {
        alert(noticeCheck.message);
        setLoading(false);
        return;
      }

      // Check librarian availability if needed
      if (bookingForm.needsLibrarian) {
        const availCheck = checkLibrarianAvailability(
          bookingForm.date,
          bookingForm.startTime,
          bookingForm.endTime
        );
        if (!availCheck.available) {
          alert(availCheck.reason);
          setLoading(false);
          return;
        }
      }

      // Generate magic link token
      const magicLinkToken = uuidv4();

      const bookingData = {
        ...bookingForm,
        magicLinkToken,
        createdAt: new Date().toISOString(),
        status: 'pending'
      };

      await addDoc(collection(db, 'users', userId, 'nornsWeavePending'), bookingData);

      const magicLink = `${window.location.origin}/calendar/booking/${magicLinkToken}`;

      // Send email notification if email is enabled
      if (settings?.emailEnabled && bookingForm.teacherEmail) {
        sendBookingRequestEmail({ ...bookingData, magicLinkToken }, settings).catch(err => {
          console.error('Failed to send booking request email:', err);
        });
      }

      alert(`Booking request submitted!\n\nYour librarian will review and approve it shortly.\n\nManage your booking here:\n${magicLink}`);

      // Reset form
      setBookingForm({
        type: 'class',
        teacherName: '',
        teacherEmail: '',
        grade: '',
        studentCount: '',
        purpose: '',
        needsLibrarian: false,
        lessonRequest: '',
        notes: '',
        library: 'elementary',
        date: '',
        startTime: '',
        endTime: '',
        isRecurring: false,
        recurringDay: 'Monday',
        recurringEndDate: '',
        services: {
          researchSupport: false,
          bookCheckout: false,
          projectorScreen: false,
          bookSetsNeeded: false,
          bookSetsDetails: '',
          aideHelpRequested: false,
          aideHelpDetails: '',
          other: false,
          otherDetails: ''
        }
      });
      setBookingStep(1);
      setShowBookingForm(false);
      setSelectedSlot(null);
      await loadPendingBookings();
    } catch (err) {
      console.error('Error submitting booking:', err);
      alert('Error submitting booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId, isPending = false) => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      try {
        const userId = sessionStorage.getItem('nornsWeaveUserId');
        const collectionName = isPending ? 'nornsWeavePending' : 'nornsWeaveBookings';

        // Find the booking to get email details
        const bookingToCancel = isPending
          ? pendingBookings.find(b => b.id === bookingId)
          : bookings.find(b => b.id === bookingId);

        await deleteDoc(doc(db, 'users', userId, collectionName, bookingId));

        // Send cancellation email if email is enabled and we have booking data
        if (settings?.emailEnabled && bookingToCancel?.teacherEmail) {
          sendBookingCancellationEmail(bookingToCancel, settings).catch(err => {
            console.error('Failed to send cancellation email:', err);
          });
        }

        await loadBookings();
        await loadPendingBookings();
        alert('Booking cancelled successfully.');
      } catch (err) {
        console.error('Error cancelling booking:', err);
        alert('Error cancelling booking.');
      }
    }
  };

  const getWeekDates = (offset = 0) => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1) + (offset * 7);
    const monday = new Date(today);
    monday.setDate(diff);

    const week = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      // Format as YYYY-MM-DD using local date to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const dayNum = String(date.getDate()).padStart(2, '0');
      week.push(`${year}-${month}-${dayNum}`);
    }
    return week;
  };

  // Check if we can navigate to the previous week (stay within academic year)
  const canNavigatePrevious = () => {
    if (!yearSetupSettings.academicYearStart) return true;
    const firstDayOfWeek = getWeekDates(weekOffset - 1)[0];
    return firstDayOfWeek >= yearSetupSettings.academicYearStart;
  };

  // Check if we can navigate to the next week (stay within academic year)
  const canNavigateNext = () => {
    if (!yearSetupSettings.academicYearEnd) return true;
    const lastDayOfWeek = getWeekDates(weekOffset + 1)[4];
    return lastDayOfWeek <= yearSetupSettings.academicYearEnd;
  };

  const getDayName = (dateStr) => {
    // Parse date string as local date to avoid timezone issues
    // When parsing 'YYYY-MM-DD', JavaScript treats it as UTC, which can shift the day
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
  };

  const doTimesOverlap = (start1, end1, start2, end2) => {
    return (start1 < end2 && end1 > start2);
  };

  const matchesDay = (itemDay, checkDayName) => {
    if (itemDay === 'everyday') {
      return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(checkDayName);
    }
    return itemDay === checkDayName;
  };

  // Helper to check if a date has a full-day closure, holiday, or workday
  const getFullDayClosureInfo = (date) => {
    // Check for full-day closures (from admin closures)
    const fullDayClosure = settings?.closures?.find(closure => {
      if (!closure.startDate || !closure.endDate) return false;
      return date >= closure.startDate && date <= closure.endDate;
    });
    if (fullDayClosure) {
      const reasonLabel = fullDayClosure.reason || 'Library Closed';
      return { isClosed: true, label: `🚫 ${reasonLabel}`, type: 'closure', data: fullDayClosure };
    }

    // Check for holidays and library-closed events
    const closedCalEvent = schoolCalendarEvents.find(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      return isInDateRange && (calEvent.type === 'holiday' || calEvent.libraryStatus === 'closed');
    });
    if (closedCalEvent) {
      const label = closedCalEvent.type === 'holiday'
        ? `🏖️ ${closedCalEvent.name}`
        : `🚫 ${closedCalEvent.name}`;
      return { isClosed: true, label, type: closedCalEvent.type, data: closedCalEvent };
    }

    // Check for workdays (teachers working, no students)
    const workdayEvent = schoolCalendarEvents.find(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      return isInDateRange && calEvent.type === 'workday';
    });
    if (workdayEvent) {
      return { isClosed: false, isWorkday: true, label: `📋 ${workdayEvent.name}`, type: 'workday', data: workdayEvent };
    }

    // Check for "outside school days" - within academic year but before/after school days
    const { schoolStartDate, schoolEndDate, academicYearStart, academicYearEnd } = yearSetupSettings;
    if (schoolStartDate && schoolEndDate && academicYearStart && academicYearEnd) {
      const isWithinAcademicYear = date >= academicYearStart && date <= academicYearEnd;
      const isOutsideSchoolDays = date < schoolStartDate || date > schoolEndDate;

      if (isWithinAcademicYear && isOutsideSchoolDays) {
        return { isClosed: false, isWorkday: true, label: '📋 Teacher Workday', type: 'outsideSchool' };
      }
    }

    return { isClosed: false, isWorkday: false };
  };

  const getEventsForSlot = (library, date, startTime, endTime) => {
    const dayName = getDayName(date);
    const events = [];

    // Add approved bookings
    bookings.forEach(b => {
      if ((b.library === library || b.library === 'both') &&
          b.date === date &&
          doTimesOverlap(startTime, endTime, b.startTime, b.endTime)) {
        events.push({ ...b, eventType: 'booking' });
      }
    });

    // Check if this date has a holiday or closure (skip all fixed classes)
    const hasHolidayOrClosure = schoolCalendarEvents.some(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      return isInDateRange && (calEvent.type === 'holiday' || calEvent.libraryStatus === 'closed');
    });

    // Check if this date is a workday (teachers working, no students - hide fixed classes)
    const hasWorkday = schoolCalendarEvents.some(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      return isInDateRange && calEvent.type === 'workday';
    });

    // Check if date is outside school days (within academic year but before/after school)
    const { schoolStartDate, schoolEndDate, academicYearStart, academicYearEnd } = yearSetupSettings;
    const isOutsideSchoolDays = schoolStartDate && schoolEndDate && academicYearStart && academicYearEnd &&
      date >= academicYearStart && date <= academicYearEnd &&
      (date < schoolStartDate || date > schoolEndDate);

    // Add fixed/recurring classes from Firestore collection (unless closure, workday, or outside school days)
    if (!hasHolidayOrClosure && !hasWorkday && !isOutsideSchoolDays) {
      fixedClasses.forEach(c => {
        // Check if this date is skipped in exceptions (handle both object and string formats)
        const isSkipped = c.exceptions?.some(ex => {
          const exDate = typeof ex === 'string' ? ex : ex.date;
          const exType = typeof ex === 'string' ? 'skipped' : ex.type;
          return exDate === date && exType === 'skipped';
        });
        if (isSkipped) return;

        // Check if class matches this day (dayOfWeek can be 'everyday' or a specific day)
        if ((c.library === library || c.library === 'both') &&
            matchesDay(c.dayOfWeek, dayName) &&
            doTimesOverlap(startTime, endTime, c.startTime, c.endTime)) {
          events.push({ ...c, eventType: 'class' });
        }
      });
    }

    // Add librarian blocks
    settings?.librarianBlocks?.forEach(b => {
      if ((b.library === library || b.library === 'both') &&
          matchesDay(b.day, dayName) &&
          doTimesOverlap(startTime, endTime, b.startTime, b.endTime)) {
        events.push({ ...b, eventType: 'block' });
      }
    });

    // Add holy days (mass time blocked)
    holyDays.forEach(holyDay => {
      if (holyDay.date === date) {
        const massStart = yearSetupSettings.massStartTime || '09:00';
        const massEnd = yearSetupSettings.massEndTime || '10:00';
        if (doTimesOverlap(startTime, endTime, massStart, massEnd)) {
          events.push({ ...holyDay, eventType: 'holyDay', displayLabel: `⛪ ${holyDay.name}`, startTime: massStart, endTime: massEnd });
        }
      }
    });

    // Add adoration days (mass time blocked)
    adorationDays.forEach(adorationDay => {
      if (adorationDay.date === date) {
        const massStart = yearSetupSettings.massStartTime || '09:00';
        const massEnd = yearSetupSettings.massEndTime || '10:00';
        if (doTimesOverlap(startTime, endTime, massStart, massEnd)) {
          events.push({ ...adorationDay, eventType: 'adoration', displayLabel: '🙏 Adoration', startTime: massStart, endTime: massEnd });
        }
      }
    });

    // Add regular mass block on mass day (Thursday by default)
    const regularMassDay = yearSetupSettings.massDay || 'Thursday';
    if (dayName === regularMassDay) {
      const massStart = yearSetupSettings.massStartTime || '09:00';
      const massEnd = yearSetupSettings.massEndTime || '10:00';

      // Check if mass has shifted this week due to holy day
      const dateObj = new Date(date + 'T00:00:00');
      const weekStart = new Date(dateObj);
      weekStart.setDate(dateObj.getDate() - dateObj.getDay() + 1); // Monday
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 4); // Friday

      const holyDayShiftingMass = holyDays.find(hd => {
        const hdDate = new Date(hd.date + 'T00:00:00');
        const isInWeek = hdDate >= weekStart && hdDate <= weekEnd;
        return isInWeek && hd.shiftsMass;
      });

      if (doTimesOverlap(startTime, endTime, massStart, massEnd)) {
        if (holyDayShiftingMass) {
          // Mass moved to holy day - slot is simply open for bookings (no indicator needed)
        } else {
          // Regular mass block
          events.push({
            id: `regular-mass-${date}`,
            startTime: massStart,
            endTime: massEnd,
            eventType: 'mass',
            displayLabel: '⛪ Mass'
          });
        }
      }
    }

    // Add school calendar events (holidays, half days, library events)
    schoolCalendarEvents.forEach(calEvent => {
      const eventStart = calEvent.startDate;
      const eventEnd = calEvent.endDate || calEvent.startDate;
      const isInDateRange = date >= eventStart && date <= eventEnd;
      if (!isInDateRange) return;

      if (calEvent.type === 'holiday' || calEvent.libraryStatus === 'closed') {
        // Full day closure - block all slots
        events.push({ ...calEvent, eventType: 'holiday', displayLabel: `🏖️ ${calEvent.name}`, startTime: startTime, endTime: endTime });
      } else if (calEvent.type === 'half-day') {
        // Half day - only block affected period
        const isAfternoon = calEvent.halfDayPeriod === 'afternoon';
        const blockStart = isAfternoon ? '12:00' : '07:00';
        const blockEnd = isAfternoon ? '17:00' : '12:00';
        if (doTimesOverlap(startTime, endTime, blockStart, blockEnd)) {
          const label = isAfternoon ? 'Early Dismissal' : 'AM Only';
          events.push({ ...calEvent, eventType: 'halfDay', displayLabel: `⏰ ${calEvent.name} - ${label}`, startTime: blockStart, endTime: blockEnd });
        }
      } else {
        // Library events (book fair, TLA, etc) - full day block
        events.push({ ...calEvent, eventType: 'libraryEvent', displayLabel: `📌 ${calEvent.name}`, startTime: startTime, endTime: endTime });
      }
    });

    // Add mass time blocks (ad-hoc)
    massTimeBlocks.forEach(massBlock => {
      if (massBlock.date === date) {
        const blockStart = massBlock.useMassTime
          ? (yearSetupSettings.massStartTime || '09:00')
          : massBlock.customStartTime;
        const blockEnd = massBlock.useMassTime
          ? (yearSetupSettings.massEndTime || '10:00')
          : massBlock.customEndTime;
        if (doTimesOverlap(startTime, endTime, blockStart, blockEnd)) {
          events.push({ ...massBlock, eventType: 'massBlock', displayLabel: `⛪ ${massBlock.reason || 'Mass'}`, startTime: blockStart, endTime: blockEnd });
        }
      }
    });

    return events;
  };

  // Get library hours (for absolute positioning calendar)
  const getLibraryHours = (library) => {
    if (library === 'elementary') {
      return {
        start: settings?.elementaryHours?.start || '07:30',
        end: settings?.elementaryHours?.end || '16:00'
      };
    } else {
      return {
        start: settings?.middleSchoolHours?.start || '08:00',
        end: settings?.middleSchoolHours?.end || '15:30'
      };
    }
  };

  // Convert time string to minutes
  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  // Get event color based on event type
  const getEventColor = (event) => {
    if (event.eventType === 'booking') {
      return event.needsLibrarian ? '#a855f7' : '#22c55e'; // purple-500 or green-500
    }
    if (event.eventType === 'class') return '#3b82f6'; // blue-500
    if (event.eventType === 'block') return '#9ca3af'; // gray-400
    if (event.eventType === 'holyDay') return '#7c3aed'; // violet-600
    if (event.eventType === 'adoration') return '#8b5cf6'; // violet-500
    if (event.eventType === 'holiday') return '#dc2626'; // red-600
    if (event.eventType === 'halfDay') return '#f97316'; // orange-500
    if (event.eventType === 'libraryEvent') return '#eab308'; // yellow-500
    if (event.eventType === 'massBlock') return '#7c3aed'; // violet-600
    if (event.eventType === 'mass') return '#7c3aed'; // violet-600
    return '#9ca3af'; // gray-400
  };

  // Calculate position percentages for absolute positioning of events
  const getEventPosition = (eventStart, eventEnd, dayStart, dayEnd) => {
    const [startHour] = dayStart.split(':').map(Number);
    const [endHour] = dayEnd.split(':').map(Number);
    const gridStartMinutes = startHour * 60;
    const gridEndMinutes = endHour * 60;
    const totalGridMinutes = gridEndMinutes - gridStartMinutes;

    const eventStartMinutes = timeToMinutes(eventStart);
    const eventEndMinutes = timeToMinutes(eventEnd);

    // Clamp to grid bounds
    const clampedStart = Math.max(eventStartMinutes, gridStartMinutes);
    const clampedEnd = Math.min(eventEndMinutes, gridEndMinutes);

    const topPercent = ((clampedStart - gridStartMinutes) / totalGridMinutes) * 100;
    const heightPercent = ((clampedEnd - clampedStart) / totalGridMinutes) * 100;

    // Ensure minimum height for readability (at least 2%)
    const minHeight = 2;
    const finalHeight = Math.max(heightPercent, minHeight);

    return { top: topPercent, height: finalHeight };
  };

  // Get all unique events for a specific day (for absolute positioning)
  const getAllEventsForDay = (library, date) => {
    const allEvents = [];
    const eventIds = new Set();

    // Use time slots to collect all events
    const slots = generateTimeSlots();
    for (let i = 0; i < slots.length - 1; i++) {
      const events = getEventsForSlot(library, date, slots[i], slots[i + 1]);
      events.forEach(event => {
        // Create a unique ID for deduplication
        const eventId = event.id || `${event.eventType}-${event.startTime}-${event.endTime}-${event.teacherName || event.reason || event.name || ''}`;
        if (!eventIds.has(eventId)) {
          eventIds.add(eventId);
          allEvents.push(event);
        }
      });
    }

    return allEvents;
  };

  // Generate hour-only time slots for cleaner grid labels
  const generateHourSlots = (library) => {
    const hours = getLibraryHours(library);
    const [startHour] = hours.start.split(':').map(Number);
    const [endHour] = hours.end.split(':').map(Number);

    const slots = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  };

  const isSlotAvailable = (library, date, startTime, endTime) => {
    const events = getEventsForSlot(library, date, startTime, endTime);
    // Slot is unavailable if there are blocking events (librarian blocks, holy days, holidays, mass, etc)
    // Check if any blocking events overlap with this time slot
    // Note: libraryEvent is NOT a blocking type - it's informational only
    const blockingTypes = ['block', 'holyDay', 'adoration', 'holiday', 'halfDay', 'massBlock', 'mass'];
    return events.filter(e => blockingTypes.includes(e.eventType)).length === 0;
  };

  const handleSlotClick = (library, date, startTime, endTime) => {
    if (!isSlotAvailable(library, date, startTime, endTime)) {
      return; // Don't allow booking blocked slots
    }

    setSelectedSlot({ library, date, startTime, endTime });
    setBookingForm(prev => ({
      ...prev,
      library,
      date,
      startTime,
      endTime
    }));
    setShowBookingForm(true);
    setBookingStep(1);
  };

  const generateTimeSlots = () => {
    const slots = [];
    slots.push('07:30');
    for (let hour = 8; hour <= 15; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < 15) slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    slots.push('16:00');
    return slots;
  };

  const timeSlots = generateTimeSlots();
  const weekDates = getWeekDates(weekOffset);
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const getWeekLabel = () => {
    if (weekOffset === 0) return 'This Week';
    if (weekOffset === 1) return 'Next Week';
    if (weekOffset === -1) return 'Last Week';
    const startDate = new Date(weekDates[0]);
    return startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const openBookingModal = () => {
    setSelectedSlot(null);
    setBookingForm(prev => ({
      ...prev,
      date: '',
      startTime: '',
      endTime: '',
      library: 'elementary'
    }));
    setShowBookingForm(true);
    setBookingStep(1);
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 border border-blue-100">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">📚</div>
            <h1 className="text-3xl font-bold text-blue-900 mb-2">
              Library Booking
            </h1>
            <p className="text-blue-600">Enter your credentials to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-blue-900 text-sm font-semibold mb-2">
                Username
              </label>
              <input
                type="text"
                required
                placeholder="Enter your username"
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-blue-900 text-sm font-semibold mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 pr-12 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-blue-600">
            <p>Need access? Contact your school librarian</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div>
              <h1 className="text-lg sm:text-2xl font-bold tracking-wide mb-1">{settings?.schoolName || 'Library Booking'}</h1>
              <p className="text-blue-100 text-sm">Managed by {settings?.yourName || 'School Librarian'}</p>
            </div>
            <button
              onClick={() => {
                sessionStorage.removeItem('nornsWeaveAuth');
                sessionStorage.removeItem('nornsWeaveUserId');
                setAuthenticated(false);
              }}
              className="text-sm sm:text-base bg-blue-800 hover:bg-blue-900 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors font-medium"
            >
              Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-3 mb-6 bg-white rounded-xl p-2 shadow-lg border border-blue-100">
          <button
            onClick={() => setView('calendar')}
            className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all ${
              view === 'calendar' ? 'bg-blue-600 text-white shadow-md' : 'text-blue-700 hover:bg-blue-50'
            }`}
          >
            📅 Calendar
          </button>
          <button
            onClick={() => setView('my-bookings')}
            className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all ${
              view === 'my-bookings' ? 'bg-blue-600 text-white shadow-md' : 'text-blue-700 hover:bg-blue-50'
            }`}
          >
            📋 My Bookings
          </button>
        </div>

        {view === 'calendar' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-6 text-sm flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-gray-700">Independent Visit</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-purple-500 rounded"></div>
                  <span className="text-gray-700">Needs Librarian</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  <span className="text-gray-700">Scheduled Class</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-400 rounded"></div>
                  <span className="text-gray-700">Unavailable</span>
                </div>
              </div>
              <button
                onClick={openBookingModal}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg"
              >
                + Book Library Time
              </button>
            </div>

            {/* Week Navigation */}
            <div className="flex justify-center items-center gap-4 mb-4">
              <button
                onClick={() => canNavigatePrevious() && setWeekOffset(prev => prev - 1)}
                disabled={!canNavigatePrevious()}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  canNavigatePrevious()
                    ? 'bg-white border-2 border-blue-200 text-blue-700 hover:bg-blue-50'
                    : 'bg-blue-50 border-2 border-blue-100 text-blue-300 cursor-not-allowed'
                }`}
              >
                ← Previous
              </button>
              <div className="bg-blue-100 px-6 py-2 rounded-lg">
                <span className="font-bold text-blue-800">{getWeekLabel()}</span>
                <span className="text-blue-600 text-sm ml-2">
                  ({weekDates[0]} - {weekDates[4]})
                </span>
              </div>
              <button
                onClick={() => canNavigateNext() && setWeekOffset(prev => prev + 1)}
                disabled={!canNavigateNext()}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  canNavigateNext()
                    ? 'bg-white border-2 border-blue-200 text-blue-700 hover:bg-blue-50'
                    : 'bg-blue-50 border-2 border-blue-100 text-blue-300 cursor-not-allowed'
                }`}
              >
                Next →
              </button>
              {weekOffset !== 0 && (
                <button
                  onClick={() => setWeekOffset(0)}
                  className="text-blue-600 hover:text-blue-800 text-sm underline"
                >
                  Today
                </button>
              )}
            </div>

            {/* Library Tabs */}
            <div className="flex justify-center mb-4">
              <div className="inline-flex bg-slate-100 rounded-xl p-1">
                <button
                  onClick={() => setActiveLibrary('elementary')}
                  className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                    activeLibrary === 'elementary'
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-200'
                  }`}
                >
                  Elementary Library
                </button>
                <button
                  onClick={() => setActiveLibrary('middle')}
                  className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                    activeLibrary === 'middle'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-200'
                  }`}
                >
                  Middle School Library
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {(() => {
                const library = activeLibrary;
                const hours = getLibraryHours(library);
                const hourSlots = generateHourSlots(library);
                const hourHeight = 60;
                const totalHours = hourSlots.length - 1;
                const calendarHeight = totalHours * hourHeight;
                const isElem = library === 'elementary';

                return (
                  <div className="flex">
                    {/* Time column */}
                    <div className={`flex-shrink-0 w-20 border-r ${isElem ? 'bg-purple-50 border-purple-200' : 'bg-indigo-50 border-indigo-200'}`}>
                      <div className={`h-14 border-b ${isElem ? 'bg-purple-100 border-purple-200' : 'bg-indigo-100 border-indigo-200'}`} />
                      {hourSlots.slice(0, -1).map((slot) => (
                        <div
                          key={slot}
                          className={`border-b text-sm text-slate-600 font-medium pr-3 flex items-start justify-end pt-1 ${isElem ? 'border-purple-100' : 'border-indigo-100'}`}
                          style={{ height: `${hourHeight}px` }}
                        >
                          {formatTime12(slot)}
                        </div>
                      ))}
                    </div>

                    {/* Day columns */}
                    <div className="flex-1 flex">
                      {weekDates.map((date, dayIdx) => {
                        const dayEvents = getAllEventsForDay(library, date);
                        const dayName = dayNames[dayIdx];
                        const fullDayInfo = getFullDayClosureInfo(date);

                        return (
                          <div key={date} className={`flex-1 border-r last:border-r-0 ${isElem ? 'border-purple-100' : 'border-indigo-100'}`}>
                            {/* Day header */}
                            <div className={`h-14 border-b flex flex-col items-center justify-center ${
                              isElem ? 'bg-purple-100 border-purple-200' : 'bg-indigo-100 border-indigo-200'
                            }`}>
                              <div className="text-sm font-bold text-slate-800">{dayName}</div>
                              <div className="text-xs text-slate-500">{date}</div>
                            </div>

                            {/* Day content container */}
                            <div className="relative" style={{ height: `${calendarHeight}px`, overflow: 'visible' }}>
                              {/* Grid lines (background) */}
                              <div className="absolute inset-0" style={{ zIndex: 0 }}>
                                {hourSlots.slice(0, -1).map((slot) => (
                                  <div
                                    key={slot}
                                    className={`border-b ${isElem ? 'border-purple-100' : 'border-indigo-100'}`}
                                    style={{ height: `${hourHeight}px` }}
                                  />
                                ))}
                              </div>

                              {/* Full-day closure overlay */}
                              {fullDayInfo.isClosed && (
                                <div
                                  className="absolute inset-0 flex flex-col items-center justify-center"
                                  style={{
                                    zIndex: 20,
                                    backgroundColor: 'rgba(239, 68, 68, 0.85)',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <div className="text-white text-center px-2">
                                    <div className="text-3xl mb-2">🚫</div>
                                    <div className="font-bold text-sm">{fullDayInfo.label}</div>
                                    <div className="text-xs opacity-80 mt-1">Library Closed</div>
                                  </div>
                                </div>
                              )}

                              {/* Workday overlay */}
                              {fullDayInfo.isWorkday && !fullDayInfo.isClosed && (
                                <div
                                  className="absolute inset-0 flex flex-col"
                                  style={{
                                    zIndex: 5,
                                    backgroundColor: 'rgba(148, 163, 184, 0.25)',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <div
                                    className="flex items-center justify-center shrink-0"
                                    style={{
                                      backgroundColor: 'rgba(100, 116, 139, 0.95)',
                                      height: '36px',
                                      pointerEvents: 'none'
                                    }}
                                  >
                                    <div className="text-white text-center text-xs font-medium">
                                      <div>{fullDayInfo.label}</div>
                                      <div className="text-[10px] opacity-80">No Classes - Bookings OK</div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Events overlay */}
                              {!fullDayInfo.isClosed && (
                                <div
                                  className="absolute inset-0"
                                  style={{ zIndex: 10, pointerEvents: 'none', overflow: 'visible' }}
                                >
                                  {dayEvents.map((event, idx) => {
                                    const pos = getEventPosition(event.startTime, event.endTime, hours.start, hours.end);
                                    const eventKey = event.id || `${event.eventType}-${event.startTime}-${event.endTime}-${event.day || ''}-${idx}`;

                                    return (
                                      <div
                                        key={eventKey}
                                        className="absolute rounded-lg text-white cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                                        style={{
                                          top: `${pos.top}%`,
                                          height: `${pos.height}%`,
                                          minHeight: '32px',
                                          left: '4px',
                                          right: '4px',
                                          zIndex: 10,
                                          backgroundColor: getEventColor(event),
                                          pointerEvents: 'auto',
                                          overflow: 'hidden'
                                        }}
                                        onClick={() => {
                                          // Check if slot is available for booking
                                          const canBook = event.eventType !== 'booking' &&
                                                         event.eventType !== 'class' &&
                                                         event.eventType !== 'block';
                                          if (canBook) {
                                            handleSlotClick(library, date, event.startTime, event.endTime);
                                          }
                                        }}
                                      >
                                        <div className="px-1 py-0.5 h-full flex flex-col">
                                          <div className="font-semibold text-[10px] leading-tight truncate">
                                            {formatTime12(event.startTime)} - {formatTime12(event.endTime)}
                                          </div>
                                          <div className="text-[10px] opacity-90 leading-tight truncate">
                                            {event.eventType === 'booking'
                                              ? `${event.grade || event.type} - ${event.teacherName}`
                                              : event.eventType === 'class'
                                              ? `${event.grade}${event.description ? ' ' + event.description : ''}`
                                              : event.eventType === 'block'
                                              ? (event.reason || 'Block')
                                              : event.displayLabel || event.title || 'Event'}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {view === 'my-bookings' && (
          <div className="bg-white rounded-xl shadow-lg p-6 border border-blue-100">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-2">My Bookings</h2>
              <p className="text-gray-600 mb-4">Enter your email address to view all your bookings, pending requests, and scheduled classes.</p>
              <div className="flex gap-4 mb-4">
                <input
                  type="email"
                  placeholder="Enter your email address..."
                  className="flex-1 px-4 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                  value={myBookingsEmail}
                  onChange={(e) => setMyBookingsEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchMyBookings(myBookingsEmail)}
                />
                <button
                  onClick={() => searchMyBookings(myBookingsEmail)}
                  disabled={myBookingsLoading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50"
                >
                  {myBookingsLoading ? 'Searching...' : 'View My Bookings'}
                </button>
              </div>
              {myBookingsError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg border border-red-200">
                  {myBookingsError}
                </div>
              )}
            </div>

            {/* Show results only after search */}
            {(myBookings.length > 0 || myFixedClasses.length > 0) && (
              <div className="space-y-8">
                {/* Fixed/Recurring Classes */}
                {myFixedClasses.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-cyan-700 mb-3 flex items-center gap-2">
                      <span className="bg-cyan-100 px-3 py-1 rounded-full">📆 Your Recurring Classes</span>
                    </h3>
                    <div className="space-y-3">
                      {myFixedClasses.map(fc => (
                        <div key={fc.id} className="border-2 border-cyan-200 rounded-lg p-4 bg-cyan-50">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-semibold text-cyan-900">
                                {fc.dayOfWeek === 'everyday' ? 'Every Day (Mon-Fri)' : `Every ${fc.dayOfWeek}`}, {formatTime12(fc.startTime)} - {formatTime12(fc.endTime)}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                {fc.grade} Grade {fc.subject && `- ${fc.subject}`} | {fc.library === 'elementary' ? 'Elementary Library' : 'Middle School Library'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Bookings (filter to future dates) */}
                {myBookings.filter(b => new Date(b.date) >= new Date().setHours(0,0,0,0)).length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-blue-700 mb-3 flex items-center gap-2">
                      <span className="bg-blue-100 px-3 py-1 rounded-full">📅 Upcoming Bookings</span>
                    </h3>
                    <div className="space-y-3">
                      {myBookings
                        .filter(b => new Date(b.date) >= new Date().setHours(0,0,0,0))
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map(booking => (
                          <div key={booking.id} className={`border-2 rounded-lg p-4 ${booking.status === 'pending' ? 'border-yellow-200 bg-yellow-50' : 'border-green-200 bg-green-50'}`}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                    booking.library === 'elementary' ? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'
                                  }`}>
                                    {booking.library === 'elementary' ? 'Elementary' : 'Middle School'}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                    booking.status === 'pending' ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'
                                  }`}>
                                    {booking.status === 'pending' ? '⏳ Pending' : '✓ Approved'}
                                  </span>
                                </div>
                                <div className="font-semibold text-gray-900">
                                  {new Date(booking.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}, {formatTime12(booking.startTime)} - {formatTime12(booking.endTime)}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {booking.grade && `${booking.grade} Grade`} {booking.purpose && `- ${booking.purpose}`}
                                </div>
                              </div>
                              {booking.status !== 'pending' && (
                                <button
                                  onClick={() => handleCancelMyBooking(booking)}
                                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Past Bookings (last 30 days) */}
                {myBookings.filter(b => {
                  const bookingDate = new Date(b.date);
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                  return bookingDate < new Date().setHours(0,0,0,0) && bookingDate >= thirtyDaysAgo;
                }).length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-500 mb-3 flex items-center gap-2">
                      <span className="bg-gray-100 px-3 py-1 rounded-full">📋 Past Bookings (Last 30 Days)</span>
                    </h3>
                    <div className="space-y-2">
                      {myBookings
                        .filter(b => {
                          const bookingDate = new Date(b.date);
                          const thirtyDaysAgo = new Date();
                          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                          return bookingDate < new Date().setHours(0,0,0,0) && bookingDate >= thirtyDaysAgo;
                        })
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map(booking => (
                          <div key={booking.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50 text-sm">
                            <span className="text-gray-600">
                              {new Date(booking.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {booking.startTime}
                            </span>
                            <span className="text-gray-800 ml-2">{booking.grade && `${booking.grade} Grade`}</span>
                            <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                              booking.library === 'elementary' ? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'
                            }`}>
                              {booking.library === 'elementary' ? 'Elementary' : 'Middle'}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty state when no search done */}
            {myBookings.length === 0 && myFixedClasses.length === 0 && !myBookingsError && !myBookingsLoading && (
              <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                <div className="text-6xl mb-4">📧</div>
                <p className="text-gray-700 text-lg font-medium">Enter your email to view your bookings</p>
                <p className="text-gray-500 text-sm mt-2">You'll see all your pending, approved, and recurring bookings</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Multi-Step Booking Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto border-2 border-blue-200">
            {/* Progress indicator */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-blue-900">Book Library Time</h3>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4].map(step => (
                  <div
                    key={step}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      bookingStep === step
                        ? 'bg-blue-600 text-white'
                        : bookingStep > step
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {bookingStep > step ? '✓' : step}
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmitBooking}>
              {/* Step 1: Booking Type */}
              {bookingStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-blue-900 text-lg font-semibold mb-4">What type of booking is this?</label>
                    <div className="space-y-3">
                      <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        bookingForm.type === 'admin' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                      }`}>
                        <input
                          type="radio"
                          name="bookingType"
                          value="admin"
                          checked={bookingForm.type === 'admin'}
                          onChange={(e) => setBookingForm({...bookingForm, type: e.target.value, needsLibrarian: false})}
                          className="w-5 h-5 mt-0.5"
                        />
                        <div>
                          <span className="font-semibold text-gray-900 block">🏢 Admin Use</span>
                          <span className="text-gray-600 text-sm">Meeting, work session, etc. (48 hours notice required)</span>
                        </div>
                      </label>
                      <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        bookingForm.type === 'class' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                      }`}>
                        <input
                          type="radio"
                          name="bookingType"
                          value="class"
                          checked={bookingForm.type === 'class'}
                          onChange={(e) => setBookingForm({...bookingForm, type: e.target.value})}
                          className="w-5 h-5 mt-0.5"
                        />
                        <div>
                          <span className="font-semibold text-gray-900 block">👨‍🏫 Class Visit</span>
                          <span className="text-gray-600 text-sm">Bring students to the library (1 week notice required)</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {bookingForm.type === 'class' && (
                    <div>
                      <label className="block text-blue-900 text-lg font-semibold mb-4">Do you need the librarian for this visit?</label>
                      <div className="space-y-3">
                        <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          bookingForm.needsLibrarian ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                        }`}>
                          <input
                            type="radio"
                            name="needsLibrarian"
                            value="yes"
                            checked={bookingForm.needsLibrarian === true}
                            onChange={() => setBookingForm({...bookingForm, needsLibrarian: true})}
                            className="w-5 h-5 mt-0.5"
                          />
                          <div>
                            <span className="font-semibold text-gray-900 block">👤 Yes - I need the librarian to teach or assist</span>
                            <span className="text-gray-600 text-sm">The librarian will be present for instruction or support</span>
                          </div>
                        </label>
                        <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          bookingForm.needsLibrarian === false ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                        }`}>
                          <input
                            type="radio"
                            name="needsLibrarian"
                            value="no"
                            checked={bookingForm.needsLibrarian === false}
                            onChange={() => setBookingForm({...bookingForm, needsLibrarian: false, lessonRequest: ''})}
                            className="w-5 h-5 mt-0.5"
                          />
                          <div>
                            <span className="font-semibold text-gray-900 block">✓ No - Independent visit</span>
                            <span className="text-gray-600 text-sm">Using the space only (checkout, quiet work, etc.)</span>
                          </div>
                        </label>
                      </div>

                      {bookingForm.needsLibrarian && (
                        <div className="mt-4">
                          <label className="block text-blue-900 text-sm font-semibold mb-1">What would you like the librarian to teach or cover?</label>
                          <textarea
                            className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                            rows="3"
                            placeholder="Describe the lesson or topic you'd like covered..."
                            value={bookingForm.lessonRequest}
                            onChange={(e) => setBookingForm({...bookingForm, lessonRequest: e.target.value})}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowBookingForm(false); setBookingStep(1); }}
                      className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => setBookingStep(2)}
                      className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Services Needed */}
              {bookingStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-blue-900 text-lg font-semibold mb-4">What do you need for this visit?</label>
                    <p className="text-gray-600 text-sm mb-4">Check all that apply</p>

                    <div className="space-y-3">
                      {/* Research support - only for class visits with librarian */}
                      {bookingForm.type === 'class' && bookingForm.needsLibrarian && (
                        <div className="border-2 border-gray-200 rounded-lg p-3">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={bookingForm.services.researchSupport}
                              onChange={(e) => setBookingForm({
                                ...bookingForm,
                                services: {...bookingForm.services, researchSupport: e.target.checked, researchDetails: e.target.checked ? bookingForm.services.researchDetails : ''}
                              })}
                              className="w-5 h-5"
                            />
                            <span className="text-gray-900">📚 Research support needed</span>
                          </label>
                          {bookingForm.services.researchSupport && (
                            <textarea
                              placeholder="Describe the research topic, read-aloud request, or lesson details..."
                              className="w-full mt-2 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 outline-none"
                              rows="2"
                              value={bookingForm.services.researchDetails || ''}
                              onChange={(e) => setBookingForm({
                                ...bookingForm,
                                services: {...bookingForm.services, researchDetails: e.target.value}
                              })}
                            />
                          )}
                        </div>
                      )}

                      {/* Book checkout - class visits only */}
                      {bookingForm.type === 'class' && (
                        <label className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={bookingForm.services.bookCheckout}
                            onChange={(e) => setBookingForm({
                              ...bookingForm,
                              services: {...bookingForm.services, bookCheckout: e.target.checked}
                            })}
                            className="w-5 h-5"
                          />
                          <span className="text-gray-900">📖 Book checkout at end of class</span>
                        </label>
                      )}

                      {/* Projector/screen - all types */}
                      <label className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bookingForm.services.projectorScreen}
                          onChange={(e) => setBookingForm({
                            ...bookingForm,
                            services: {...bookingForm.services, projectorScreen: e.target.checked}
                          })}
                          className="w-5 h-5"
                        />
                        <span className="text-gray-900">📽️ Projector/screen needed</span>
                      </label>

                      {/* Book sets - class visits only */}
                      {bookingForm.type === 'class' && (
                        <div className="border-2 border-gray-200 rounded-lg p-3">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={bookingForm.services.bookSetsNeeded}
                              onChange={(e) => setBookingForm({
                                ...bookingForm,
                                services: {...bookingForm.services, bookSetsNeeded: e.target.checked, bookSetsDetails: e.target.checked ? bookingForm.services.bookSetsDetails : ''}
                              })}
                              className="w-5 h-5"
                            />
                            <span className="text-gray-900">📚 Book sets needed</span>
                          </label>
                          {bookingForm.services.bookSetsNeeded && (
                            <input
                              type="text"
                              placeholder="Which books or sets?"
                              className="w-full mt-2 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 outline-none"
                              value={bookingForm.services.bookSetsDetails}
                              onChange={(e) => setBookingForm({
                                ...bookingForm,
                                services: {...bookingForm.services, bookSetsDetails: e.target.value}
                              })}
                            />
                          )}
                        </div>
                      )}

                      {/* Aide help - class visits only */}
                      {bookingForm.type === 'class' && (
                        <div className="border-2 border-gray-200 rounded-lg p-3">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={bookingForm.services.aideHelpRequested}
                              onChange={(e) => setBookingForm({
                                ...bookingForm,
                                services: {...bookingForm.services, aideHelpRequested: e.target.checked, aideHelpDetails: e.target.checked ? bookingForm.services.aideHelpDetails : ''}
                              })}
                              className="w-5 h-5"
                            />
                            <span className="text-gray-900">🙋 Request library aide help</span>
                          </label>
                          {bookingForm.services.aideHelpRequested && (
                            <input
                              type="text"
                              placeholder="What would you like help with?"
                              className="w-full mt-2 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 outline-none"
                              value={bookingForm.services.aideHelpDetails}
                              onChange={(e) => setBookingForm({
                                ...bookingForm,
                                services: {...bookingForm.services, aideHelpDetails: e.target.value}
                              })}
                            />
                          )}
                        </div>
                      )}

                      {/* Other - all types */}
                      <div className="border-2 border-gray-200 rounded-lg p-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={bookingForm.services.other}
                            onChange={(e) => setBookingForm({
                              ...bookingForm,
                              services: {...bookingForm.services, other: e.target.checked, otherDetails: e.target.checked ? bookingForm.services.otherDetails : ''}
                            })}
                            className="w-5 h-5"
                          />
                          <span className="text-gray-900">📝 Other</span>
                        </label>
                        {bookingForm.services.other && (
                          <input
                            type="text"
                            placeholder="Please describe"
                            className="w-full mt-2 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 outline-none"
                            value={bookingForm.services.otherDetails}
                            onChange={(e) => setBookingForm({
                              ...bookingForm,
                              services: {...bookingForm.services, otherDetails: e.target.value}
                            })}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setBookingStep(1)}
                      className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setBookingStep(3)}
                      className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Date/Time and Details */}
              {bookingStep === 3 && (
                <div className="space-y-4">
                  {selectedSlot && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-blue-800 font-medium">
                        Selected: {selectedSlot.library === 'elementary' ? '📚 Elementary' : '📖 Middle School'} Library
                        <br />
                        {new Date(selectedSlot.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        {' '}{formatTime12(selectedSlot.startTime)} - {formatTime12(selectedSlot.endTime)}
                      </p>
                    </div>
                  )}

                  {!selectedSlot && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-blue-900 text-sm font-semibold mb-1">Library *</label>
                          <select
                            required
                            className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                            value={bookingForm.library}
                            onChange={(e) => setBookingForm({...bookingForm, library: e.target.value})}
                          >
                            <option value="elementary">Elementary</option>
                            <option value="middle">Middle School</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-blue-900 text-sm font-semibold mb-1">Date *</label>
                          <input
                            type="date"
                            required
                            className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                            value={bookingForm.date}
                            onChange={(e) => setBookingForm({...bookingForm, date: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <TimePicker
                          label="Start Time"
                          value={bookingForm.startTime}
                          onChange={(val) => setBookingForm({...bookingForm, startTime: val})}
                          required
                        />
                        <TimePicker
                          label="End Time"
                          value={bookingForm.endTime}
                          onChange={(val) => setBookingForm({...bookingForm, endTime: val})}
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* Recurring Booking Option - Only for class bookings without a pre-selected slot */}
                  {bookingForm.type === 'class' && !selectedSlot && (
                    <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-lg p-4">
                      <label className="flex items-center gap-3 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={bookingForm.isRecurring}
                          onChange={(e) => setBookingForm({...bookingForm, isRecurring: e.target.checked})}
                          className="w-5 h-5 rounded"
                        />
                        <span className="font-semibold text-cyan-800">🔄 Make this a recurring weekly booking</span>
                      </label>
                      {bookingForm.isRecurring && (
                        <div className="space-y-3 ml-8 mt-3 pt-3 border-t border-cyan-200">
                          <p className="text-sm text-cyan-700">
                            Request the same time slot every week. Your librarian will review and can approve this as a fixed recurring class.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-cyan-800 text-sm font-medium mb-1">Repeat Every</label>
                              <select
                                className="w-full px-3 py-2 border-2 border-cyan-200 rounded-lg focus:border-cyan-500 outline-none"
                                value={bookingForm.recurringDay}
                                onChange={(e) => setBookingForm({...bookingForm, recurringDay: e.target.value})}
                              >
                                <option value="Monday">Monday</option>
                                <option value="Tuesday">Tuesday</option>
                                <option value="Wednesday">Wednesday</option>
                                <option value="Thursday">Thursday</option>
                                <option value="Friday">Friday</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-cyan-800 text-sm font-medium mb-1">Until (optional)</label>
                              <input
                                type="date"
                                className="w-full px-3 py-2 border-2 border-cyan-200 rounded-lg focus:border-cyan-500 outline-none"
                                value={bookingForm.recurringEndDate}
                                onChange={(e) => setBookingForm({...bookingForm, recurringEndDate: e.target.value})}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-blue-900 text-sm font-semibold mb-1">Your Name *</label>
                      <input
                        type="text"
                        required
                        className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                        value={bookingForm.teacherName}
                        onChange={(e) => setBookingForm({...bookingForm, teacherName: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-blue-900 text-sm font-semibold mb-1">Your Email *</label>
                      <input
                        type="email"
                        required
                        className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                        value={bookingForm.teacherEmail}
                        onChange={(e) => {
                          setBookingForm({...bookingForm, teacherEmail: e.target.value});
                          setUserEmail(e.target.value);
                        }}
                      />
                    </div>
                  </div>

                  {bookingForm.type === 'class' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-blue-900 text-sm font-semibold mb-1">Grade/Class *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g., 3rd Grade"
                          className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                          value={bookingForm.grade}
                          onChange={(e) => setBookingForm({...bookingForm, grade: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-blue-900 text-sm font-semibold mb-1">Number of Students *</label>
                        <input
                          type="number"
                          required
                          min="1"
                          className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                          value={bookingForm.studentCount}
                          onChange={(e) => setBookingForm({...bookingForm, studentCount: e.target.value})}
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-blue-900 text-sm font-semibold mb-1">Purpose of Visit *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., Research project, Book checkout, Story time"
                      className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                      value={bookingForm.purpose}
                      onChange={(e) => setBookingForm({...bookingForm, purpose: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-blue-900 text-sm font-semibold mb-1">Additional Notes (optional)</label>
                    <textarea
                      className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                      rows="3"
                      placeholder="Any special requirements or information..."
                      value={bookingForm.notes}
                      onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setBookingStep(2)}
                      className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setBookingStep(4)}
                      className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                      Review →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Confirmation */}
              {bookingStep === 4 && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <h4 className="font-bold text-blue-900 mb-3">Booking Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type:</span>
                        <span className="font-medium">{bookingForm.type === 'admin' ? '🏢 Admin Use' : '👨‍🏫 Class Visit'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Library:</span>
                        <span className="font-medium">{bookingForm.library === 'elementary' ? '📚 Elementary' : '📖 Middle School'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">{new Date(bookingForm.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time:</span>
                        <span className="font-medium">{formatTime12(bookingForm.startTime)} - {formatTime12(bookingForm.endTime)}</span>
                      </div>
                      {bookingForm.type === 'class' && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Librarian:</span>
                            <span className="font-medium">{bookingForm.needsLibrarian ? '👤 Required' : '✓ Independent'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Grade:</span>
                            <span className="font-medium">{bookingForm.grade}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Students:</span>
                            <span className="font-medium">{bookingForm.studentCount}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Teacher:</span>
                        <span className="font-medium">{bookingForm.teacherName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Purpose:</span>
                        <span className="font-medium">{bookingForm.purpose}</span>
                      </div>
                    </div>

                    {bookingForm.isRecurring && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-cyan-600 font-semibold">🔄 Recurring Request</span>
                          <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded-full text-xs font-medium">Weekly</span>
                        </div>
                        <p className="text-sm text-gray-700">
                          Every <strong>{bookingForm.recurringDay}</strong> at {formatTime12(bookingForm.startTime)} - {formatTime12(bookingForm.endTime)}
                          {bookingForm.recurringEndDate && (
                            <span className="text-gray-500"> until {new Date(bookingForm.recurringEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          )}
                        </p>
                        <p className="text-xs text-cyan-600 mt-1">Your librarian will review and may convert this to a fixed recurring class.</p>
                      </div>
                    )}

                    {Object.values(bookingForm.services).some(v => v === true) && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-gray-600 text-sm mb-1">Services requested:</p>
                        <ul className="text-sm space-y-1">
                          {bookingForm.services.researchSupport && <li>📚 Research support</li>}
                          {bookingForm.services.bookCheckout && <li>📖 Book checkout</li>}
                          {bookingForm.services.projectorScreen && <li>📽️ Projector/screen</li>}
                          {bookingForm.services.bookSetsNeeded && <li>📚 Book sets: {bookingForm.services.bookSetsDetails}</li>}
                          {bookingForm.services.aideHelpRequested && <li>🙋 Aide help: {bookingForm.services.aideHelpDetails}</li>}
                          {bookingForm.services.other && <li>📝 Other: {bookingForm.services.otherDetails}</li>}
                        </ul>
                      </div>
                    )}

                    {bookingForm.lessonRequest && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-gray-600 text-sm mb-1">Lesson request:</p>
                        <p className="text-sm">{bookingForm.lessonRequest}</p>
                      </div>
                    )}
                  </div>

                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800 text-sm">
                      <strong>Notice:</strong> {bookingForm.type === 'admin' ? 'Admin bookings require 48 hours notice.' : 'Class bookings require 1 week notice.'}
                      <br />
                      Your librarian will review and approve your request.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setBookingStep(3)}
                      className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Submitting...' : '✓ Submit Request'}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default NornsWeave;
